import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { FinancialDirection } from './financial-report';

/** One period column, newest first. */
export interface PeriodTableColumn {
  key: string;
  /** Period end, e.g. "31 Dec 2025". */
  label: string;
  /** A muted prefix before the label, e.g. "Y.E.". */
  prefix?: string;
  /** Short lines under the period end, e.g. the accounts type and filing date. */
  details?: string[];
  url?: string;
  urlLabel?: string;
  urlAriaLabel?: string;
}

export interface PeriodTableCell {
  value: unknown;
  display: string;
  negative: boolean;
  /** Change against the next (older) period. */
  change: number | null;
  direction: FinancialDirection;
}

export interface PeriodTableRow {
  name: string;
  /** 'string' rows are text, laid out to wrap; every other type is a figure. */
  type: string;
  cells: PeriodTableCell[];
}

/**
 * Shared multi-period table: one column per period, newest first, with a sticky metric column and header, a Latest badge,
 * brackets or colour for negatives, an en dash for blanks and an optional year-on-year change under each figure.
 * The Tax financial report and the Tax energy and emissions history both use it. Skins theme it with --profile-report-*.
 * Place it in a flex column with a bounded height so that the table scrolls inside its rounded frame.
 */
@Component({
  selector: 'company-profile-period-table',
  templateUrl: './company-profile-period-table.component.html',
  styleUrls: ['./company-profile-period-table.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  host: { 'class': 'profile-period-table' },
})
export class CompanyProfilePeriodTableComponent {
  @Input() columns: PeriodTableColumn[] = [];
  @Input() rows: PeriodTableRow[] = [];
  /** Heading of the sticky first column. */
  @Input() metricHeading = 'Metric';
  /** Screen reader caption for the table. */
  @Input() caption = '';
  /** Accessible name of the scrolling region. */
  @Input() regionLabel = '';
  /** Toolbar summary: the bold lead, then the rest, e.g. "5 filed periods" + ", newest first · GBP". */
  @Input() summaryLead = '';
  @Input() summaryDetail = '';
  @Input() showChange = true;
  @Output() showChangeChange = new EventEmitter<boolean>();

  toggleChange(): void {
    this.showChange = !this.showChange;
    this.showChangeChange.emit(this.showChange);
  }

  isBlank(cell: PeriodTableCell): boolean {
    return cell.value === null || cell.value === undefined || cell.value === '';
  }

  /** The visible year-on-year change; "n/m" (not meaningful) when the percentage is omitted against a negative figure. */
  changeText(change: number | null, direction: FinancialDirection): string {
    if (!direction) return '';
    if (direction === 'flat') return 'No change';
    return change === null ? 'n/m' : `${change.toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`;
  }

  changeDescription(change: number | null, direction: FinancialDirection): string {
    if (!direction) return '';
    if (direction === 'flat') return 'No change on the prior period';
    const movement = direction === 'up' ? 'Up' : 'Down';
    return change === null
      ? `${movement} on the prior period; a percentage is not meaningful when either figure is negative`
      : `${movement} ${this.changeText(change, direction)} on the prior period`;
  }
}
